import * as React from 'react';
import {useDispatch} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useHistory} from 'src/rerouter/index';
import {usePageTitle} from 'src/contexts/page-title-context';
import {CustomBreadCrumbs} from 'src/components/common/custom-breadcrumbs';
import {useApiQuery} from 'src/hooks/useApiQuery';
import * as reduxApi from 'src/utils/redux-api-v2';
import {downloadCSV} from 'src/utils/download';
import {
  Table,
  BasicSingleCell,
  TableBottomBar,
} from '@spaced-out/ui-design-system/lib/components/Table';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  BodyMedium,
  TitleMedium,
  SubTitleLarge,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {PageName} from '@spaced-out/ui-design-system/lib/components/PageTitle';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {formatDate} from 'src/utils/date-time';
import type {
  CandidateToJoinEntry,
  CandidateToJoinExtras,
  CandidateToJoinApiItem,
} from 'src/types/crm';
import css from './candidates-to-join.css';


const escapeCSVValue = (val: unknown): string => {
  const str = val == null ? '' : String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const PAGE_SIZE = 10;

const CandidateNameCell = ({
  data,
  extras,
}: {
  data: CandidateToJoinEntry;
  extras?: CandidateToJoinExtras;
}) => {
  const {t} = useLabelTranslation();
  return (
    <BasicSingleCell>
      <Link
        onClick={() => extras?.onCandidateClick(data.jobId, data.candidateId)}
      >
        {data.candidateName || t('No_name_found', 'No name found')}
      </Link>
    </BasicSingleCell>
  );
};

const JobNameCell = ({data}: {data: CandidateToJoinEntry}) => (
  <BasicSingleCell>
    <BodyMedium>{data.jobName}</BodyMedium>
  </BasicSingleCell>
);

const RecruiterCell = ({data}: {data: CandidateToJoinEntry}) => (
  <BasicSingleCell>
    <BodyMedium>{data.recruiterName || '-'}</BodyMedium>
  </BasicSingleCell>
);

const JoiningDateCell = ({data}: {data: CandidateToJoinEntry}) => (
  <BasicSingleCell>
    <BodyMedium>
      {data.candidateJoiningDate
        ? formatDate(data.candidateJoiningDate, 'en')
        : '-'}
    </BodyMedium>
  </BasicSingleCell>
);

export const CandidatesToJoin = () => {
  const {t, language} = useLabelTranslation();
  const router = useHistory();
  const dispatch = useDispatch();
  const {
    actions: {setPageTitleProps},
  } = usePageTitle();

  const [page, setPage] = React.useState(0);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const downloadCsv = React.useCallback(async () => {
    setIsDownloading(true);
    try {
      const data = (await dispatch(
        reduxApi.post(
          '/api/dashboard/overview/hired-to-join',
          {},
          {page: 0, pageSize: 0},
          {apiPath: '/trmapi'},
        ),
      )) as CandidateToJoinApiItem[];

      if (!Array.isArray(data) || data.length === 0) {
        return;
      }

      const headers = [
        t('Candidate_Name', 'Candidate Name'),
        t('Job_Title', 'Job Title'),
        t('Recruiter', 'Recruiter'),
        t('Joining_Date', 'Joining Date'),
      ];

      const rows = data.map((item) =>
        [
          item.candidateName ?? '',
          item.jobName ?? '',
          item.recruiterName ?? '',
          item.candidateJoiningDate
            ? formatDate(item.candidateJoiningDate, 'en')
            : '',
        ]
          .map(escapeCSVValue)
          .join(','),
      );

      downloadCSV(
        [headers.map(escapeCSVValue).join(','), ...rows],
        'candidates-to-join.csv',
      );
    } finally {
      setIsDownloading(false);
    }
  }, [dispatch, t]);

  React.useEffect(() => {
    setPageTitleProps({
      showBackButton: true,
      onBack: () => {
        router.replace('/dashboard');
      },
      tabSlotComponent: null,
      rightSlotComponent: null,
      customPageNameComponent: (
        <PageName>
          <TitleMedium>
            {t('Candidates_to_Join', 'Candidates to Join')}
          </TitleMedium>
        </PageName>
      ),
    });
  }, [language]);

  // Fetch one extra item to determine whether a next page exists without a separate count endpoint.
  const {data: result, isLoading} = useApiQuery<CandidateToJoinApiItem[]>(
    `/api/dashboard/overview/hired-to-join?page=${page}&pageSize=${
      PAGE_SIZE + 1
    }`,
    {
      queryOptions: {
        queryKey: ['hired-to-join-list', page],
      },
      apiOptions: {
        method: 'POST',
        data: {},
        options: {apiPath: '/trmapi'},
      },
    },
  );

  const hasNextPage = Array.isArray(result) && result.length > PAGE_SIZE;

  const entries: CandidateToJoinEntry[] = React.useMemo(() => {
    if (!Array.isArray(result)) {
      return [];
    }
    return result.slice(0, PAGE_SIZE).map((item, index) => ({
      id: `${item.candidateId}-${item.jobId}-${index}`,
      candidateId: String(item.candidateId ?? ''),
      candidateName: item.candidateName ?? '',
      jobId: String(item.jobId ?? ''),
      jobName: item.jobName ?? '',
      recruiterName: item.recruiterName ?? '',
      candidateJoiningDate: item.candidateJoiningDate ?? null,
    }));
  }, [result]);

  const headers = [
    {
      label: t('Candidate_Name', 'Candidate Name'),
      key: 'candidateName' as keyof CandidateToJoinEntry,
      render: CandidateNameCell,
    },
    {
      label: t('Job_Title', 'Job Title'),
      key: 'jobName' as keyof CandidateToJoinEntry,
      render: JobNameCell,
    },
    {
      label: t('Recruiter', 'Recruiter'),
      key: 'recruiterName' as keyof CandidateToJoinEntry,
      render: RecruiterCell,
    },
    {
      label: t('Joining_Date', 'Joining Date'),
      key: 'candidateJoiningDate' as keyof CandidateToJoinEntry,
      render: JoiningDateCell,
    },
  ];

  const extras: CandidateToJoinExtras = {
    onCandidateClick: (jobId, candidateId) => {
      router.push(
        `/crm/job/${jobId}/info/applicants?applicantId=${candidateId}`,
      );
    },
  };

  const breadCrumbLinks = [
    {
      name: t('Dashboard', 'Dashboard'),
      isActive: false,
      onClick: () => router.push('/dashboard'),
    },
    {
      name: t('Candidates_to_Join', 'Candidates to Join'),
      isActive: true,
      onClick: () => router.push('/dashboard/candidates-to-join'),
    },
  ];

  return (
    <>
      <CustomBreadCrumbs breadCrumbLinks={breadCrumbLinks} />
      <div className={css.container}>
        <div className={css.titleRow}>
          <SubTitleLarge>
            {t('Candidates_to_Join', 'Candidates to Join')}
          </SubTitleLarge>
          <Button
            type="secondary"
            size="small"
            iconLeftName="arrow-to-bottom"
            isLoading={isDownloading}
            onClick={downloadCsv}
          >
            {t('Download_full_report', 'Download full report')}
          </Button>
        </div>

        <div className={css.tableContainer}>
          {/* <TableTopBar>
          <BodyMedium>
            {t('Candidates_to_Join', 'Candidates to Join')}
          </BodyMedium>
        </TableTopBar> */}
          <Table
            entries={entries}
            headers={headers}
            extras={extras}
            idName="id"
            borderRadius={'0'}
            isLoading={isLoading}
            stickyHeader
            emptyText={
              <EmptyState
                title={t('No_candidates_found', 'No candidates found')}
                description={t(
                  'There_are_no_candidates_scheduled_to_join',
                  'There are no candidates scheduled to join.',
                )}
                imageVariant="file"
              />
            }
          />
          <TableBottomBar>
            <div className={css.paginationControls}>
              <Button
                type="ghost"
                size="small"
                iconLeftName="angle-left"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              />
              <BodyMedium>{page + 1}</BodyMedium>
              <Button
                type="ghost"
                size="small"
                iconRightName="angle-right"
                disabled={!hasNextPage}
                onClick={() => {
                  if (hasNextPage) {
                    setPage((p) => p + 1);
                  }
                }}
              />
            </div>
          </TableBottomBar>
        </div>
      </div>
    </>
  );
};
